import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  BackButton,
  BusinessLoyaltyProgramSection,
  MobilePopoverCard,
  MobileFloatingButton,
  EmailInputField,
} from "halifax";
import {
  CorpSessionInfo,
  FrequentFlyerMap,
  ICorpPerson,
  IFlyerOption,
  IPerson,
  SessionInfo,
  TripDetails,
} from "redmond";
import { Divider, Typography } from "@material-ui/core";
import { FrequentFlyerForm } from "../FrequentFlyerForm";
import "./styles.scss";

export interface IFlightAdditionalInfoWorkflowProps {
  onGoBack: () => void;
  open: boolean;
  onClickContinue: (traveler: IPerson | ICorpPerson) => void;
  mobileHeaderElement?: JSX.Element;
  className?: string;
  traveler: IPerson | ICorpPerson | undefined;
  isLoyaltyEligible: boolean;
  frequentFlyerPrograms: IFlyerOption[] | null;
  tripDetails: TripDetails;
  sessionInfo: SessionInfo | CorpSessionInfo | undefined;
}

export const FlightAdditionalInfoWorkflow = ({
  onGoBack,
  open,
  onClickContinue,
  mobileHeaderElement,
  className,
  traveler,
  isLoyaltyEligible,
  frequentFlyerPrograms,
  tripDetails,
  sessionInfo,
}: IFlightAdditionalInfoWorkflowProps): React.ReactElement => {
  const [frequentFlyer, setFrequentFlyer] = useState<FrequentFlyerMap>({});
  const [contactEmail, setContactEmail] = useState<string>(
    () => traveler?.emailAddress ?? "",
  );

  useEffect(() => {
    setContactEmail(traveler?.emailAddress ?? "");
    setFrequentFlyer(traveler?.frequentFlyer ?? {});
  }, [traveler?.id, traveler?.emailAddress, traveler?.frequentFlyer]);

  const handleContinue = () => {
    if (!traveler) return;

    const updatedTraveler: IPerson | ICorpPerson = {
      ...traveler,
      emailAddress: contactEmail || traveler.emailAddress,
      frequentFlyer: {
        ...traveler.frequentFlyer,
        ...frequentFlyer,
      },
    };

    onClickContinue(updatedTraveler);
  };

  const hasFrequentFlyerValue = Object.values(frequentFlyer).some(
    (v) => v?.value,
  );

  return (
    <MobilePopoverCard
      open={open}
      className={clsx(
        "b2b flight-additional-info-workflow-root mobile",
        className,
      )}
      fullScreen
      topLeftButton={
        <BackButton
          className="flight-additional-info-back-button"
          onClick={onGoBack}
        />
      }
      headerElement={mobileHeaderElement}
    >
      <Typography component="h1" className="flight-additional-info-title">
        {`${traveler?.givenName} ${traveler?.surname}`}
      </Typography>

      {!!traveler &&
        isLoyaltyEligible &&
        frequentFlyerPrograms &&
        frequentFlyerPrograms.length > 0 && (
          <>
            <Divider />
            <FrequentFlyerForm
              isMobile
              onChange={setFrequentFlyer}
              traveler={traveler}
              frequentFlyerPrograms={frequentFlyerPrograms}
            />
          </>
        )}
      <Divider />
      <div className="form-container">
        <label htmlFor="contact-email-input-field">
          <Typography className="title" component="span">
            Contact information
          </Typography>
        </label>
        <Typography className="subtitle" variant="body2">
          A copy of the flight confirmation email will be sent to this traveler.
        </Typography>
        <EmailInputField
          fullWidth
          onChange={(value) => {
            setContactEmail(value);
          }}
          email={contactEmail}
        />
      </div>
      <Divider className="business-loyalty-section-divider" />
      <BusinessLoyaltyProgramSection
        tripDetails={tripDetails}
        sessionInfo={sessionInfo}
        isMobile
        mode="banner"
      />

      <MobileFloatingButton
        secondary={!hasFrequentFlyerValue}
        className="flight-additional-info-continue-button"
        wrapperClassName="flight-additional-info-continue-button-container"
        onClick={handleContinue}
      >
        Continue
      </MobileFloatingButton>
    </MobilePopoverCard>
  );
};
